import type { ChangeEvent } from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { inject, injectable } from "tsyringe";
import type { InputOnChangeData } from "@/components/Input/Input";
import {
  type IWarehouseApi,
  WAREHOUSE_API_TOKEN,
} from "@/api/WarehouseApi";
import { ViewModel } from "@orderofchaos/mvvm";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
} from "@/stores/navigation";
import {
  REALTIME_STORE_TOKEN,
  type IRealtimeStore,
} from "@/stores/RealtimeStore";
import {
  isTelegramMiniAppEnvironment,
  type TelegramWidgetAuthUser,
} from "@/utils/telegramWebApp";
import {
  canSubmitCredentials,
  canSubmitTelegramMiniApp,
  canUseSocialProvider as canUseSocialProviderGuard,
  canUseTelegramWidget as canUseTelegramWidgetGuard,
  isAuthBusy,
  type LoginBusyFlags,
} from "./viewmodel/guards";
import {
  initializeLoginOperation,
  submitAppleIdTokenOperation,
  submitCredentialsOperation,
  submitGoogleIdTokenOperation,
  submitTelegramMiniAppOperation,
  submitTelegramWidgetOperation,
  toggleLoginModeOperation,
  type LoginWorkflowState,
} from "./viewmodel/operations";

@injectable()
export class LoginViewModel extends ViewModel {
  @observable
  username = "";

  @observable
  password = "";

  @observable
  isSubmitting = false;

  @observable
  isTelegramSubmitting = false;

  @observable
  isGoogleSubmitting = false;

  @observable
  isAppleSubmitting = false;

  @observable
  isRegisterMode = false;

  @observable
  error: string | null = null;

  @observable
  notice: string | null = null;

  @observable
  telegramInitData = "";

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
    @inject(REALTIME_STORE_TOKEN)
    private readonly realtimeStore: IRealtimeStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get title(): string {
    return this.isRegisterMode ? "Регистрация" : "Вход в систему";
  }

  @computed
  get subtitle(): string {
    return this.isRegisterMode
      ? "Создайте пользователя и дождитесь одобрения администратора"
      : "Используйте логин или email и пароль для доступа к складу";
  }

  @computed
  get submitLabel(): string {
    return this.isRegisterMode ? "Создать аккаунт" : "Войти";
  }

  @computed
  get switchModeLabel(): string {
    return this.isRegisterMode ? "Уже есть аккаунт" : "Создать аккаунт";
  }

  @computed
  get canSubmit(): boolean {
    return canSubmitCredentials(this.authBusyFlags);
  }

  @computed
  get canTelegramSubmit(): boolean {
    return canSubmitTelegramMiniApp({
      isRegisterMode: this.isRegisterMode,
      flags: this.authBusyFlags,
      telegramInitData: this.telegramInitData,
    });
  }

  @computed
  get isTelegramMiniApp(): boolean {
    return isTelegramMiniAppEnvironment();
  }

  @computed
  get telegramBotUsername(): string {
    return String(import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "").trim();
  }

  @computed
  get canUseTelegramWidget(): boolean {
    return canUseTelegramWidgetGuard({
      isRegisterMode: this.isRegisterMode,
      isTelegramMiniApp: this.isTelegramMiniApp,
      telegramBotUsername: this.telegramBotUsername,
      flags: this.authBusyFlags,
    });
  }

  @computed
  get googleClientId(): string {
    return String(import.meta.env.VITE_GOOGLE_CLIENT_ID || "").trim();
  }

  @computed
  get appleClientId(): string {
    return String(import.meta.env.VITE_APPLE_CLIENT_ID || "").trim();
  }

  @computed
  get appleRedirectUri(): string {
    return String(import.meta.env.VITE_APPLE_REDIRECT_URI || "").trim();
  }

  @computed
  get canUseGoogleLogin(): boolean {
    return canUseSocialProviderGuard({
      isRegisterMode: this.isRegisterMode,
      isTelegramMiniApp: this.isTelegramMiniApp,
      providerClientId: this.googleClientId,
      flags: this.authBusyFlags,
    });
  }

  @computed
  get canUseAppleLogin(): boolean {
    return canUseSocialProviderGuard({
      isRegisterMode: this.isRegisterMode,
      isTelegramMiniApp: this.isTelegramMiniApp,
      providerClientId: this.appleClientId,
      flags: this.authBusyFlags,
    });
  }

  protected onViewMounted(): void {
    initializeLoginOperation({
      state: this.workflowState,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
    });
  }

  @action.bound
  onUsernameChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.username = data.value || event.target.value;
    this.error = null;
    this.notice = null;
  }

  @action.bound
  onPasswordChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.password = data.value || event.target.value;
    this.error = null;
    this.notice = null;
  }

  @action.bound
  toggleMode(): void {
    toggleLoginModeOperation({
      state: this.workflowState,
    });
  }

  @action.bound
  async submit(): Promise<void> {
    await submitCredentialsOperation({
      state: this.workflowState,
      api: this.api,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
    });
  }

  @action.bound
  async submitTelegram(): Promise<void> {
    await submitTelegramMiniAppOperation({
      state: this.workflowState,
      api: this.api,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
      canSubmit: this.canTelegramSubmit,
    });
  }

  @action.bound
  async submitTelegramFromWidget(
    payload: TelegramWidgetAuthUser,
  ): Promise<void> {
    await submitTelegramWidgetOperation({
      state: this.workflowState,
      api: this.api,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
      payload,
      isAuthLocked: this.isRegisterMode || isAuthBusy(this.authBusyFlags),
    });
  }

  @action.bound
  async submitGoogleIdToken(idToken: string): Promise<void> {
    await submitGoogleIdTokenOperation({
      state: this.workflowState,
      api: this.api,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
      idToken,
      canSubmit: this.canUseGoogleLogin,
    });
  }

  @action.bound
  async submitAppleIdToken(idToken: string): Promise<void> {
    await submitAppleIdTokenOperation({
      state: this.workflowState,
      api: this.api,
      navigationStore: this.navigationStore,
      realtimeStore: this.realtimeStore,
      idToken,
      canSubmit: this.canUseAppleLogin,
    });
  }

  @action.bound
  onSocialAuthError(message: string): void {
    const normalized = message.trim();
    if (!normalized) {
      return;
    }

    this.error = normalized;
  }

  private get authBusyFlags(): LoginBusyFlags {
    return {
      isSubmitting: this.isSubmitting,
      isTelegramSubmitting: this.isTelegramSubmitting,
      isGoogleSubmitting: this.isGoogleSubmitting,
      isAppleSubmitting: this.isAppleSubmitting,
    };
  }

  private get workflowState(): LoginWorkflowState {
    return this;
  }
}
